import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
    Box,
    Button,
    Typography,
    Link,
    ThemeProvider,
    Breadcrumbs,
    Card,
    CardContent,
    CardHeader,
    Snackbar,
    Alert,
    CircularProgress,
} from '@mui/material';
import { createTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EventIcon from '@mui/icons-material/Event';
import { getJobOrders } from '../API/jobOrderAPI';
import BaseFormWrapper from "../../components/shared/BaseFormWrapperL1";

const localizer = momentLocalizer(moment);

const theme = createTheme({
    palette: {
        primary: {
            main: "#1e5d45", // Custom primary color
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    backgroundColor: "#1e5d45",
                    color: "white",
                    '&:hover': {
                        backgroundColor: "#14432e", // Slightly darker on hover
                    },
                },
            },
        },
    },
});

const CustomToolbar = (toolbar) => {
    const { label, view } = toolbar; // Access the current view
    const handleNavigate = (direction) => {
        toolbar.onNavigate(direction);
        // window.location.reload();
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ display: 'flex', gap: '2px' }}>
                <button
                    onClick={() => handleNavigate('TODAY')}
                    style={{
                        backgroundColor: "#1e5d45",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "5px 10px",
                        cursor: "pointer",
                    }}
                >
                    Today
                </button>
                <button
                    onClick={() => handleNavigate('PREV')}
                    style={{
                        backgroundColor: "#1e5d45",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "5px 10px",
                        cursor: "pointer",
                    }}
                >
                    Back
                </button>
                <button
                    onClick={() => handleNavigate('NEXT')}
                    style={{
                        backgroundColor: "#1e5d45",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "5px 10px",
                        cursor: "pointer",
                    }}
                >
                    Next
                </button>
            </div>
            <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                {label}
            </div>
            <div style={{ display: 'flex', gap: '2px' }}>
                <button
                    onClick={() => toolbar.onView('month')}
                    style={{
                        backgroundColor: view === 'month' ? "#14432e" : "#1e5d45", // Darker color for active
                        color: "white",
                        border: view === 'month' ? "2px solid white" : "none", // Border for active
                        borderRadius: "4px",
                        padding: "5px 10px",
                        cursor: "pointer",
                    }}
                >
                    Month
                </button>
                <button
                    onClick={() => toolbar.onView('week')}
                    style={{
                        backgroundColor: view === 'week' ? "#14432e" : "#1e5d45", // Darker color for active
                        color: "white",
                        border: view === 'week' ? "2px solid white" : "none", // Border for active
                        borderRadius: "4px",
                        padding: "5px 10px",
                        cursor: "pointer",
                    }}
                >
                    Week
                </button>
                <button
                    onClick={() => toolbar.onView('day')}
                    style={{
                        backgroundColor: view === 'day' ? "#14432e" : "#1e5d45", // Darker color for active
                        color: "white",
                        border: view === 'day' ? "2px solid white" : "none", // Border for active
                        borderRadius: "4px",
                        padding: "5px 10px",
                        cursor: "pointer",
                    }}
                >
                    Day
                </button>
            </div>
        </div>
    );
};


function ViewCalendar() {
    const [jobOrders, setJobOrders] = useState([]);
    // const [view, setView] = useState('month');
    // const [date, setDate] = useState(new Date());
    const [view, setView] = useState(() => localStorage.getItem('calendarView') || 'month');
    const [date, setDate] = useState(() => {
        const savedDate = localStorage.getItem('calendarDate');
        return savedDate ? new Date(savedDate) : new Date(); // Default to the current date
    });

    // Create a new state variable for the month in "YYYY-MM" format
    const [month, setMonth] = useState(() => {
        const savedMonth = localStorage.getItem('calendarMonth');
        if (savedMonth) {
            return savedMonth;
        }
        const now = new Date();
        return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const locView = localStorage.getItem('calendarView')
    const savedDate = localStorage.getItem('calendarDate')


    // const [view, setView] = useState('day'); // Default to 'day' view
    // const [date, setDate] = useState(new Date(2025, 0, 10)); // Default to January 10, 2025 (0-based month index)




    const handleViewChange = (newView) => {
        setView(newView);
        localStorage.setItem('calendarView', newView); // Save view to localStorage
    };

    const handleDateChange = (newDate) => {
        setDate(newDate);
        localStorage.setItem('calendarDate', newDate.toISOString()); // Save full date
        // Derive calendarMonth in "YYYY-MM" format from the new date
        const calendarMonth = `${newDate.getFullYear()}-${(newDate.getMonth() + 1)
            .toString()
            .padStart(2, '0')}`;
        localStorage.setItem('calendarMonth', calendarMonth);
        setMonth(calendarMonth)
    };




    useEffect(() => {
        const [year, monthNum] = month.split('-').map(Number);

        // Create Date objects for the first and last day of the month
        const start = new Date(year, monthNum - 1, 1);
        const end = new Date(year, monthNum, 0);

        // Helper function to format dates as "YYYY-MM-DD"
        const formatDate = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        // Derive startDate and endDate as strings
        const startDate = formatDate(start);
        const endDate = formatDate(end);

        const fetchJobOrders = async () => {
            setIsLoading(true);
            const orders = await getJobOrders([], [], [], startDate, endDate, null);
            setJobOrders(orders);
            setIsLoading(false);
        };

        fetchJobOrders();
    }, [month]); // Runs whenever `month` changes


    const convertToDateObject = (dateStr, timeStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const [hours, minutes] = timeStr.split(':').map(Number);
        return new Date(year, month - 1, day, hours, minutes);
    };


    const scheduleData = jobOrders
        .filter(record => record.job_date && record.start_time && record.end_time)
        .map(record => ({
            id: record.id,
            title: record.client.name,
            start: convertToDateObject(record.job_date, record.start_time),
            end: convertToDateObject(record.job_date, record.end_time),
            endorsement_form_id: record.endorsement_form_id || 'none',
            technicians: record.technicians || []
        }));


    const colorPool = [
        '#455A64', // Steel Blue
        '#D99382', // Coral Pink
        '#B5C68B', // Pale Olive
        '#354F52', // Deep Forest Green
        '#E1B87A', // Sandy Beige
        '#6C7A89', // Gray-Teal
        '#A89D91', // Muted Taupe
        '#215D77',
        '#D5B59C', // Beige-Brown
        '#A87C66', // Chestnut        
        '#E6A9A6', // Soft Rose
        '#536F4F', // Dark Olive Green
        '#CBB59B', // Light Brown
        '#BF884C',
        '#5E8A57', // Forest Green
        '#D8C1A5',
        '#76A16B', // Fresh Green
        '#9BB87E', // Lime Olive
        '#90B4A4', // Muted Teal
    ];


    const generateColorMapping = (events) => {
        const colorMap = {};
        let colorIndex = 0;

        events.forEach(event => {
            if (!colorMap[event.endorsement_form_id]) {
                colorMap[event.endorsement_form_id] = colorPool[colorIndex % colorPool.length];
                colorIndex += 1;
            }
        });

        return colorMap;
    };

    const colorMap = useMemo(() => generateColorMapping(scheduleData), [scheduleData]);

    // Event Prop Getter: Skip background color for month view
    const eventPropGetter = (event) => {
        if (view === 'month') {
            return {
                style: {
                    color: 'white',
                    fontWeight: 'bold',
                    padding: '4px',
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                },
            };
        }

        const backgroundColor = event.endorsement_form_id === 'none'
            ? '#808080'// Gray color for None endorsement_form_id
            : colorMap[event.endorsement_form_id];

        return {
            style: {
                backgroundColor,
                color: 'white',
                borderRadius: '4px',
                padding: '4px',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
            },
        };
    };

    const monthViewEvents = useMemo(() => {
        const counts = scheduleData.reduce((acc, event) => {
            const day = moment(event.start).format('YYYY-MM-DD');
            acc[day] = (acc[day] || 0) + 1;
            return acc;
        }, {});

        return Object.entries(counts).map(([day, count]) => ({
            title: `${count} Jobs`,
            start: new Date(day),
            end: new Date(day),
            isSummary: true,
        }));
    }, [scheduleData]);

    const events = useMemo(() => (view === 'month' ? monthViewEvents : scheduleData), [view, monthViewEvents, scheduleData]);


    const CustomEvent = ({ event }) => {
        const handleClick = () => {
            setDate(event.start);
            setView('day');
        };

        if (view === 'month') {
            return (
                <Box
                    onClick={handleClick}
                    sx={{
                        color: 'white',
                        borderRadius: '4px',
                        padding: '8px 4px',
                        fontSize: '12px',
                        textAlign: 'center',
                        cursor: 'pointer',
                    }}
                >
                    {event.title}
                </Box>
            );
        }

        if (view === 'week') {
            return (
                <Box
                    onClick={handleClick}
                    sx={{
                        backgroundColor: event.endorsement_form_id === 'none'
                            ? '#808080' // Force gray if endorsement_form_id is 'none'
                            : colorMap[event.endorsement_form_id] || '#808080', // Existing logic
                        color: 'white',
                        padding: '2px 4px',
                        fontSize: '12px',
                        textAlign: 'left',
                        borderRadius: '4px',
                        cursor: 'pointer',
                    }}
                >
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                        {event.title}
                    </Typography>
                    {event.notes && (
                        <Typography variant="caption" sx={{ fontSize: '10px' }}>
                            {event.notes}
                        </Typography>
                    )}
                </Box>
            );
        }

        if (view === 'day') {
            localStorage.setItem('calendarDate', date.toISOString()); // Save date to localStorage
            localStorage.setItem('calendarView', view); // Save view to localStorage
            return (
                <Box
                    sx={{
                        backgroundColor: event.endorsement_form_id === 'none'
                            ? '#808080' // Force gray if endorsement_form_id is 'none'
                            : colorMap[event.endorsement_form_id] || '#808080', // Existing logic
                        color: 'white',
                        padding: '4px',
                        fontSize: '12px',
                        borderRadius: '4px',
                        display: 'block',
                    }}
                >
                    {/* Event Title as a Link */}
                    <Link
                        href={`/view-job-order/${event.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{
                            color: 'white',
                            textDecoration: 'underline',
                        }}
                    >
                        {event.title}
                    </Link>
                    {/* Technicians List Outside the Link */}
                    {event.technicians?.length > 0 && (
                        <Box sx={{ marginTop: '4px' }}>
                            {event.technicians.map((tech, index) => (
                                <span
                                    key={tech.id}
                                    style={{
                                        fontSize: '11px',
                                        textDecoration: 'none', // Ensure no underline
                                        color: 'inherit', // Match parent text color
                                    }}
                                >
                                    {tech.firstname} {tech.lastname}
                                    {index < event.technicians.length - 1 ? ', ' : ''}
                                </span>
                            ))}
                        </Box>
                    )}

                </Box>
            );
        }


        return null;
    };



    return (

        <BaseFormWrapper
            titleIcon={<EventIcon sx={{ fontSize: "30px", marginRight: "8px" }} />}
            title="Operations"
            subTitle="Calendar"
            formTitle={moment().format("dddd, MMMM DD, YYYY")}
            subTitleRoute="/view-calendar"
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >
            <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                date={date}
                view={view}
                // onNavigate={(newDate) => setDate(newDate)}
                // onView={(newView) => setView(newView)}
                onNavigate={handleDateChange}
                onView={handleViewChange}
                style={{ height: 600, marginTop: "-20px" }}
                views={['month', 'week', 'day']}
                components={{
                    toolbar: CustomToolbar, // Use the custom toolbar
                    event: CustomEvent,
                }}
                eventPropGetter={eventPropGetter}
                tooltipAccessor={(event) => {
                    // Format the technicians list for the tooltip
                    const technicianNames = Array.isArray(event.technicians)
                        ? event.technicians.map(tech => `${tech.firstname} ${tech.lastname}`).join(', ')
                        : 'No Technicians Assigned';

                    return `Technicians: ${technicianNames}`;
                }}


            />
        </BaseFormWrapper>
    );
}

export default ViewCalendar;
